import React, { useMemo } from 'react';
import {
  Dropdown,
  Bubble,
  SearchBox,
} from 'tea-component';
import FilterSelectButton from './components/FilterSelectButton';
import FilterFooterButtons from './components/FilterFooterButton';
import RadioGroup from './components/RadioGroup';
import OptionsGroup from './components/OptionsGroup';
import useFilterData from './hooks/useFilterData';
import useFilterSearch from './hooks/useFilterSearch';
import useFilterLocale from './useLocale';
import { TOptionsItem, ISelectorMultipleProps } from './types';
import { LOCALE_CN } from './config';
import '@/pages/home/assets/scss/components/selectComponents.scss';
import './index.scss';

const FilterSelect = (props: ISelectorMultipleProps) => {
  const {
    className = '',
    boxClassName = '',
    overlayClassName = '',
    boxStyle = {},
    listWidth,
    listHeight,
    style,
    name,
    placeholder = '',
    showSlectedNum = 2,
    tooltip,
    button,
    virtual = true,
    showSelectAll = true,
    minSelect,
    maxSelect,
    locale = LOCALE_CN,
    language,
    searchable = true,
    searchValue: propsSearchValue = '',
    group,
    groupByKey = 'type',
    onSearchValueChange,
    onConfirm,
    isRevertToLastSelection = false,
    onOpen,
    onClose,
    bubblePlacement = 'top',
    ...restProps
  } = props;

  const { formatMessage } = useFilterLocale(locale, language);
  const {
    optionsValue,
    radioValue,
    isOpen,
    options,
    radioOptions,
    handleSelectAll,
    handleClearAll,
    handleOpen,
    handleClose,
    handleChange,
    handleRadioChange,
    setIsOpen,
    setValue
  } = useFilterData(props);

  const {
    searchValue,
    renderOptions,
    renderRadioOptions,
    handleSearchValueChange,
  } = useFilterSearch({ searchValue: propsSearchValue, onSearchValueChange, options, radioOptions });
  
  // 筛选器文本框处理
  const filterButton = useMemo(() => {
    // 筛选项options处理
    const selectedOptions: Array<TOptionsItem> = [];
    [...(radioOptions || []), ...(options || [])]?.forEach((optionItem: TOptionsItem) => {
      if ([radioValue, ...(optionsValue || [])]?.includes(optionItem?.value)) {
        selectedOptions?.push(optionItem);
      }
    });
    // 传入button可使用ReactNode和函数方式渲染
    if (button) return typeof button === 'function' ? button(selectedOptions) : button;
    // 默认的筛选器文本框渲染
    return <FilterSelectButton
      name={name}
      selectedOptions={selectedOptions}
      showSlectedNum={showSlectedNum}
      placeholder={placeholder}
    />;
  }, [optionsValue, radioValue, placeholder, radioOptions, options, button, name]);

  const { isMatch, isClear, isShowSelectAll } = useMemo(() => {
    // 是否搜索为空
    const isMatch = renderOptions?.length > 0 || renderRadioOptions?.length > 0;
    // 是否为清除按钮
    const isClear = renderOptions?.some((item) => optionsValue?.includes(item?.value));
    // 是否展示select all和clear all
    const isShowSelectAll = showSelectAll && renderOptions?.length > 0 && !minSelect && !maxSelect;
    return { isMatch, isClear, isShowSelectAll };
  }, [maxSelect, minSelect, optionsValue, renderOptions, renderRadioOptions, showSelectAll]);

  return (
    <div className='dbs-filter-select'>
      <Bubble
        dark
        escapeWithReference
        flipped={false}
        content={tooltip && !isOpen ? tooltip : null}
        placement={bubblePlacement}
      >
        <Dropdown
          className={`db-filter-select ${className}`}
          boxClassName={` ${boxClassName}`}
          overlayClassName={` ${overlayClassName}`}
          boxStyle={listWidth ? { ...boxStyle, width: `${listWidth}px` } : { ...boxStyle }}
          style={style}
          appearance="button"
          button={filterButton}
          onOpen={() => {
            // 清空操作放在close中会导致结果闪动的问题，所以放在open方法中处理
            !propsSearchValue && handleSearchValueChange('');
            handleOpen();
          }}
          open={isOpen}
          updateOnChildrenChange={true}
          onClose={handleClose}
          {...restProps}
        >
          {
            searchable && <SearchBox
              placeholder={formatMessage({ id: 'placeholder' })}
              className="db-filter-select-search"
              onChange={handleSearchValueChange}
              value={searchValue}
            />
          }
          <div className="db-filter-select-content">
            <RadioGroup
              value={radioValue}
              options={renderRadioOptions}
              formatMessage={formatMessage}
              onChange={handleRadioChange}
            />
            {isShowSelectAll && (
              <div className="db-filter-select-options-title">
                <span className="db-filter-select-options-title-lf">{name ? name : formatMessage({ id: 'radioGroupTitle' })}</span>
                <span className="db-filter-select-options-title-rt" onClick={() => isClear ? handleClearAll(renderOptions) : handleSelectAll(renderOptions)}>
                  {isClear ? formatMessage({ id: 'clear_all' }) : formatMessage({ id: 'select_all' })}
                </span>
              </div>
            )}
            {options?.length > 0 ? <OptionsGroup
              value={optionsValue}
              virtual={virtual}
              listHeight={listHeight}
              isMatch={isMatch}
              options={renderOptions}
              formatMessage={formatMessage}
              onChange={handleChange}
              group={group}
              groupByKey={groupByKey}
            // /> : <div className="db-filter-no-data">{formatMessage({ id: 'no_data' })}</div>
            /> : null
            }
          </div>
          {onConfirm && !((optionsValue.length === 0 && searchValue)) && (
            <FilterFooterButtons
              onConfirm={onConfirm}
              onCancel={handleClose}
              optionsValue={optionsValue}
              searchValue={searchValue}
              formatMessage={formatMessage}
              setIsOpen={setIsOpen}
              setValue={setValue}
              isRevertToLastSelection={isRevertToLastSelection}
              {...restProps}
            />
          )}
        </Dropdown>
      </Bubble>
    </div>
  );
};

export default FilterSelect;